<script setup lang="ts">
/*
 * Unified Employer create/edit form (V2-FE-2).
 *
 * One form for every Employer Kind — main company, subsidiary, OS client, OS
 * vendor, OS project. Labels adapt to `kind` (CLAUDE/plan decision #7):
 *   - COMPANY / OS_CLIENT / OS_VENDOR → "Company full name" + "Company short name"
 *   - OS_PROJECT                      → "Project full name" + "Project short name"
 *                                       + a read-only Client field (the parent)
 *
 * `EmployeeIdFormat` defaults to `<ShortName>-000000` (the BE does the same on
 * create) and is editable. The form always persists `Organization.{FullName,
 * ShortName}` + the `Kind` discriminator.
 *
 * The form is presentation-only: it emits `submit` with a built
 * `EmployerRequest`. The owning surface (Organization → Profile editor, or
 * FE-3's Outsourcing tab) handles the create/update mutation + the Drawer.
 */
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Plus, Trash2 } from 'lucide-vue-next';
import { Button, FormField, IconButton, TextInput } from '@/ui';
import type {
  BaseLocation,
  Employer,
  EmployerKind,
  EmployerRequest,
} from '@/api/services/employers';
import BaseLocationField from './BaseLocationField.vue';

const props = withDefaults(
  defineProps<{
    /** Discriminator the form is creating/editing. Drives the labels. */
    kind: Exclude<EmployerKind, 'UNKNOWN'>;
    /** Existing employer when editing; omit for create. */
    employer?: Employer | null;
    /** For OS_PROJECT: the parent OS_CLIENT/OS_VENDOR id (sent as ParentId). */
    parentId?: string | null;
    /** For OS_PROJECT: the parent's display name (read-only Client field). */
    parentName?: string | null;
    /** Disables the submit button while the parent's mutation is in-flight. */
    saving?: boolean;
  }>(),
  { employer: null, parentId: null, parentName: null, saving: false },
);

const emit = defineEmits<{ submit: [body: EmployerRequest]; cancel: [] }>();

const { t } = useI18n();

const isProject = computed(() => props.kind === 'OS_PROJECT');
const isEdit = computed(() => Boolean(props.employer));

// The Kind the form emits is always exactly the `kind` prop the caller pinned.
// "Create new company" (from the scope switcher) always passes `COMPANY`; the
// Outsourcing tab passes OS_CLIENT/OS_VENDOR/OS_PROJECT explicitly. There is no
// persona toggle on the company-create surface — a tenant creates clients/vendors
// from Organization → Outsourcing (gated on the provider claim), never here.
const resolvedKind = computed<Exclude<EmployerKind, 'UNKNOWN'>>(() => props.kind);

// The richer "Create New Company" surface (matching v1's EmployerFormV2) — the
// Email / Phone / Representative repeaters + the embedded base-location map —
// renders ONLY when creating a plain COMPANY. OS_PROJECT stays trimmed; edit mode
// is excluded (BaseLocation is a create-only payload in v1, and the company-detail
// editor owns location edits separately).
const isCompanyCreate = computed(() => props.kind === 'COMPANY' && !isEdit.value);

interface FormState {
  fullName: string;
  shortName: string;
  industry: string;
  employeeIdFormat: string;
  website: string;
  address: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  notes: string;
}

function emptyState(): FormState {
  return {
    fullName: '',
    shortName: '',
    industry: '',
    employeeIdFormat: '',
    website: '',
    address: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    notes: '',
  };
}

const form = reactive<FormState>(emptyState());
// Tracks whether the user has hand-edited the EmployeeIdFormat — once they
// have, we stop auto-deriving it from the short name.
const idFormatTouched = ref(false);
const submitted = ref(false);

// --- Company-create repeaters + base location (v1 parity) -----------------
// Repeatable Email / Phone / Representative rows, mirroring v1's
// EmployerFormV2. Reactive arrays so add/remove just splice. The base location
// is the structured `BaseLocation` from the embedded Leaflet picker.
const emails = ref<string[]>(['']);
const phones = ref<string[]>(['']);
const representatives = ref<string[]>(['']);
const baseLocation = ref<BaseLocation | null>(null);

type RepeaterKey = 'emails' | 'phones' | 'representatives';
const repeaters: Record<RepeaterKey, typeof emails> = { emails, phones, representatives };

function addRow(key: RepeaterKey): void {
  // Always append a fresh empty row. (The previous guard refused to add a row
  // while the last one was blank, which made the "add" button silently no-op on
  // a fresh form — the reported bug.) Blank rows are dropped at submit time.
  repeaters[key].value.push('');
}
function removeRow(key: RepeaterKey, index: number): void {
  const rows = repeaters[key];
  rows.value.splice(index, 1);
  if (rows.value.length === 0) rows.value.push('');
}

/** Hydrate the form when an existing employer is supplied (edit mode). */
function hydrate(): void {
  const e = props.employer;
  if (!e) {
    Object.assign(form, emptyState());
    emails.value = [''];
    phones.value = [''];
    representatives.value = [''];
    baseLocation.value = null;
    idFormatTouched.value = false;
    submitted.value = false;
    return;
  }
  Object.assign(form, {
    fullName: e.name,
    shortName: e.shortName === e.name ? '' : e.shortName,
    industry: e.industry,
    employeeIdFormat: e.employeeIdFormat,
    website: e.website,
    address: e.address,
    contactName: e.primaryContact.name,
    contactEmail: e.primaryContact.email,
    contactPhone: e.primaryContact.phone,
    notes: e.notes,
  });
  idFormatTouched.value = Boolean(e.employeeIdFormat);
  submitted.value = false;
}

watch(() => props.employer, hydrate, { immediate: true });

// Auto-derive `<ShortName>-000000` until the operator overrides it.
watch(
  () => form.shortName,
  (short) => {
    if (idFormatTouched.value) return;
    const prefix = short.trim();
    form.employeeIdFormat = prefix ? `${prefix}-000000` : '';
  },
);

function onIdFormatInput(v: string | number): void {
  idFormatTouched.value = true;
  form.employeeIdFormat = String(v);
}

// --- Validation ---
const fullNameError = computed(() =>
  submitted.value && !form.fullName.trim() ? t('v2.organization.form.errors.full-name') : '',
);
const emailError = computed(() => {
  if (!submitted.value) return '';
  const email = form.contactEmail.trim();
  if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    return t('v2.organization.form.errors.email');
  }
  return '';
});

// On the company-create surface the base location is required (v1 blocks submit
// until a location with a name + valid coordinates is set).
const baseLocationMissing = computed(() => isCompanyCreate.value && baseLocation.value === null);
const isValid = computed(
  () => Boolean(form.fullName.trim()) && !emailError.value && !baseLocationMissing.value,
);

const fullNameLabel = computed(() =>
  isProject.value
    ? t('v2.organization.form.project-full-name')
    : t('v2.organization.form.company-full-name'),
);
const shortNameLabel = computed(() =>
  isProject.value
    ? t('v2.organization.form.project-short-name')
    : t('v2.organization.form.company-short-name'),
);

function onSubmit(): void {
  submitted.value = true;
  if (!isValid.value) return;
  const body: EmployerRequest = {
    Organization: {
      FullName: form.fullName.trim(),
      ShortName: form.shortName.trim() || undefined,
    },
    Kind: resolvedKind.value,
  };
  if (isProject.value && props.parentId) body.ParentId = props.parentId;
  if (isProject.value && form.fullName.trim()) body.ProjectName = form.fullName.trim();
  if (form.industry.trim()) body.Industry = form.industry.trim();
  if (form.employeeIdFormat.trim()) body.EmployeeIdFormat = form.employeeIdFormat.trim();
  if (form.website.trim()) body.Website = form.website.trim();
  if (form.address.trim()) body.Address = form.address.trim();
  if (form.notes.trim()) body.Notes = form.notes.trim();
  const contact = {
    Name: form.contactName.trim() || undefined,
    Email: form.contactEmail.trim() || undefined,
    Phone: form.contactPhone.trim() || undefined,
  };
  if (contact.Name || contact.Email || contact.Phone) body.PrimaryContact = contact;
  // Company-create extras: repeatable Emails / PhoneNumbers / Representatives +
  // the embedded base location (v1 "Create New Company" parity).
  if (isCompanyCreate.value) {
    const cleanEmails = emails.value.map((v) => v.trim()).filter(Boolean);
    const cleanPhones = phones.value.map((v) => v.trim()).filter(Boolean);
    const cleanReps = representatives.value.map((v) => v.trim()).filter(Boolean);
    if (cleanEmails.length) body.Emails = cleanEmails;
    if (cleanPhones.length) body.PhoneNumbers = cleanPhones;
    if (cleanReps.length) body.Representatives = cleanReps;
    if (baseLocation.value) body.BaseLocation = baseLocation.value;
  }
  emit('submit', body);
}
</script>

<template>
  <form class="flex flex-col gap-5" data-testid="employer-form" @submit.prevent="onSubmit">
    <!-- OS_PROJECT: read-only Client (parent) field -->
    <FormField v-if="isProject" :label="t('v2.organization.form.client')">
      <template #default="{ id }">
        <TextInput
          :id="id"
          :model-value="parentName ?? ''"
          disabled
          data-testid="employer-form-client"
          :placeholder="t('v2.organization.form.client-none')"
        />
      </template>
    </FormField>

    <FormField :label="fullNameLabel" required :error="fullNameError">
      <template #default="{ id, describedBy, invalid }">
        <TextInput
          :id="id"
          v-model="form.fullName"
          :aria-describedby="describedBy"
          :aria-invalid="invalid"
          data-testid="employer-form-full-name"
        />
      </template>
    </FormField>

    <FormField :label="shortNameLabel" :hint="t('v2.organization.form.short-name-hint')">
      <template #default="{ id }">
        <TextInput :id="id" v-model="form.shortName" data-testid="employer-form-short-name" />
      </template>
    </FormField>

    <!-- Projects inherit every other field from their parent client/vendor, so
         the project form is just the two names. Everything below is hidden for
         OS_PROJECT and only rendered for COMPANY / OS_CLIENT / OS_VENDOR. -->
    <template v-if="!isProject">
      <FormField
        :label="t('v2.organization.form.employee-id-format')"
        :hint="t('v2.organization.form.employee-id-format-hint')"
      >
        <template #default="{ id }">
          <TextInput
            :id="id"
            :model-value="form.employeeIdFormat"
            data-testid="employer-form-id-format"
            @update:model-value="onIdFormatInput"
          />
        </template>
      </FormField>

      <FormField :label="t('v2.organization.form.industry')">
        <template #default="{ id }">
          <TextInput :id="id" v-model="form.industry" data-testid="employer-form-industry" />
        </template>
      </FormField>

      <FormField :label="t('v2.organization.form.website')">
        <template #default="{ id }">
          <TextInput
            :id="id"
            v-model="form.website"
            type="url"
            placeholder="https://"
            data-testid="employer-form-website"
          />
        </template>
      </FormField>

      <FormField :label="t('v2.organization.form.address')">
        <template #default="{ id }">
          <TextInput :id="id" v-model="form.address" data-testid="employer-form-address" />
        </template>
      </FormField>

      <fieldset
        class="flex flex-col gap-4 rounded-xl border border-app-border p-4"
        data-testid="employer-form-primary-contact"
      >
        <legend class="px-1 text-sm font-semibold text-app-fg">
          {{ t('v2.organization.form.primary-contact') }}
        </legend>
        <FormField :label="t('v2.organization.form.contact-name')">
          <template #default="{ id }">
            <TextInput :id="id" v-model="form.contactName" />
          </template>
        </FormField>
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField :label="t('v2.organization.form.contact-email')" :error="emailError">
            <template #default="{ id, describedBy, invalid }">
              <TextInput
                :id="id"
                v-model="form.contactEmail"
                type="email"
                :aria-describedby="describedBy"
                :aria-invalid="invalid"
                data-testid="employer-form-contact-email"
              />
            </template>
          </FormField>
          <FormField :label="t('v2.organization.form.contact-phone')">
            <template #default="{ id }">
              <TextInput :id="id" v-model="form.contactPhone" type="tel" />
            </template>
          </FormField>
        </div>
      </fieldset>

      <!-- Company-create parity with v1 "Create New Company": repeatable Email /
           Phone / Representative rows + the embedded base-location map. Rendered
           only when creating a plain COMPANY. -->
      <template v-if="isCompanyCreate">
        <fieldset
          class="flex flex-col gap-3 rounded-xl border border-app-border p-4"
          data-testid="employer-form-emails"
        >
          <legend class="px-1 text-sm font-semibold text-app-fg">
            {{ t('v2.organization.company-form.emails') }}
          </legend>
          <div v-for="(_, i) in emails" :key="`email-${i}`" class="flex items-end gap-2">
            <FormField
              class="flex-1"
              :label="t('v2.organization.company-form.email-n', { n: i + 1 })"
            >
              <template #default="{ id }">
                <TextInput
                  :id="id"
                  v-model="emails[i]"
                  type="email"
                  :data-testid="`employer-form-email-${i}`"
                />
              </template>
            </FormField>
            <IconButton
              v-if="emails.length > 1"
              :label="t('v2.common.remove')"
              :data-testid="`employer-form-email-remove-${i}`"
              @click="removeRow('emails', i)"
            >
              <Trash2 class="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
          <button
            type="button"
            class="self-end text-sm font-medium text-primary hover:text-primary-700"
            data-testid="employer-form-email-add"
            @click="addRow('emails')"
          >
            <Plus class="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />{{
              t('v2.organization.company-form.add-email')
            }}
          </button>
        </fieldset>

        <fieldset
          class="flex flex-col gap-3 rounded-xl border border-app-border p-4"
          data-testid="employer-form-phones"
        >
          <legend class="px-1 text-sm font-semibold text-app-fg">
            {{ t('v2.organization.company-form.phones') }}
          </legend>
          <div v-for="(_, i) in phones" :key="`phone-${i}`" class="flex items-end gap-2">
            <FormField
              class="flex-1"
              :label="t('v2.organization.company-form.phone-n', { n: i + 1 })"
            >
              <template #default="{ id }">
                <TextInput
                  :id="id"
                  v-model="phones[i]"
                  type="tel"
                  :data-testid="`employer-form-phone-${i}`"
                />
              </template>
            </FormField>
            <IconButton
              v-if="phones.length > 1"
              :label="t('v2.common.remove')"
              :data-testid="`employer-form-phone-remove-${i}`"
              @click="removeRow('phones', i)"
            >
              <Trash2 class="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
          <button
            type="button"
            class="self-end text-sm font-medium text-primary hover:text-primary-700"
            data-testid="employer-form-phone-add"
            @click="addRow('phones')"
          >
            <Plus class="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />{{
              t('v2.organization.company-form.add-phone')
            }}
          </button>
        </fieldset>

        <fieldset
          class="flex flex-col gap-3 rounded-xl border border-app-border p-4"
          data-testid="employer-form-representatives"
        >
          <legend class="px-1 text-sm font-semibold text-app-fg">
            {{ t('v2.organization.company-form.representatives') }}
          </legend>
          <div v-for="(_, i) in representatives" :key="`rep-${i}`" class="flex items-end gap-2">
            <FormField
              class="flex-1"
              :label="t('v2.organization.company-form.representative-n', { n: i + 1 })"
            >
              <template #default="{ id }">
                <TextInput
                  :id="id"
                  v-model="representatives[i]"
                  :data-testid="`employer-form-representative-${i}`"
                />
              </template>
            </FormField>
            <IconButton
              v-if="representatives.length > 1"
              :label="t('v2.common.remove')"
              :data-testid="`employer-form-representative-remove-${i}`"
              @click="removeRow('representatives', i)"
            >
              <Trash2 class="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
          <button
            type="button"
            class="self-end text-sm font-medium text-primary hover:text-primary-700"
            data-testid="employer-form-representative-add"
            @click="addRow('representatives')"
          >
            <Plus class="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />{{
              t('v2.organization.company-form.add-representative')
            }}
          </button>
        </fieldset>

        <BaseLocationField v-model="baseLocation" required :show-error="submitted" />
      </template>
    </template>

    <div class="flex items-center justify-end gap-2 pt-1">
      <Button type="button" variant="outline" @click="emit('cancel')">
        {{ t('v2.common.cancel') }}
      </Button>
      <Button type="submit" variant="primary" :disabled="saving" data-testid="employer-form-submit">
        {{ isEdit ? t('v2.common.save') : t('v2.organization.form.create') }}
      </Button>
    </div>
  </form>
</template>
